import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';
import { stripePromise } from '../../../../utils/stripe';
import { StyledDataGrid } from '../../../../constants/styling';
import { panelSx, PT } from '../../../../components/portalUi';
import { UpdateCardForm } from './UpdateCardForm';

/* Why migration-status says a subscription cannot be moved YET, for the reasons that
 * mean it still bills on the OLD Stripe account (server: assessLegacyMigration).
 * 'none' has no old subscription at all; 'paused' means the old account is taking
 * every payment right now, so nothing is "old" to explain; 'error' says nothing.
 *
 * 'cancelling' is deliberately NOT here, because it answers for two different people:
 * a member who is leaving, and a member who has just MOVED — migrating sets the old
 * subscription to cancel at its period end. Telling the second group their
 * subscription is billed on the old system would be the opposite of true. */
const STILL_ON_OLD_BILLING = [
  'unpaid',
  'renews_too_soon',
  'bad_status',
  'stripe_missing'
];

export const SettingsUserPayments = () => {
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();

  // Card-update / migration flow state.
  const [starting, setStarting] = useState(false);
  const [needsMigration, setNeedsMigration] = useState(false);
  const [migrationReason, setMigrationReason] = useState<string | null>(null);
  /* The card list shows the new billing system's cards only (server:
   * purchaseCardSource), so a subscription still billing on the old system has its
   * card somewhere this page does not list. Say so, rather than let "no saved payment
   * methods" read as if nothing is being charged. */
  const subscriptionOnOldSystem =
    needsMigration || STILL_ON_OLD_BILLING.includes(migrationReason ?? '');
  const [payment, setPayment] = useState<{
    clientSecret: string;
    publishableKey?: string;
    mode: 'update' | 'migrate';
  } | null>(null);

  const fetchData = async () => {
    try {
      const result = await post('user/transactions', {}, true);

      setPaymentMethods(result.cards || []);
      // Merge physical-card orders into the same Transactions grid (Type = 'Physical
      // Card'), newest first. The grid's Type column reads `transactionType`, so map
      // the Transaction model's `type` onto it for the regular rows too.
      const merged = [
        ...(result.transactions || []).map((t: any) => ({
          ...t,
          transactionType: t.transactionType ?? t.type
        })),
        ...(result.physicalCardOrders || [])
      ].sort(
        (a: any, b: any) =>
          new Date(b.transactionDate).getTime() -
          new Date(a.transactionDate).getTime()
      );
      setTransactions(merged);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    try {
      const ms = await get('user/subscription/migration-status', true);
      setNeedsMigration(!!ms?.needsMigration);
      setMigrationReason(typeof ms?.reason === 'string' ? ms.reason : null);
    } catch {
      setNeedsMigration(false);
      setMigrationReason(null);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startUpdate = async () => {
    setStarting(true);
    try {
      const res = await post('user/payment-method/setup-intent', {}, true);
      if (!res?.success || !res?.clientSecret) {
        showSnackbar(res?.message || 'Could not start the card update.', 'error');
        return;
      }
      setPayment({
        clientSecret: res.clientSecret,
        publishableKey: res.publishableKey,
        mode: 'update'
      });
    } catch (e) {
      showSnackbar('Could not start the card update. Please try again.', 'error');
    } finally {
      setStarting(false);
    }
  };

  // Collect a card on the NEW account (migration:true); the form then moves the
  // subscription over via user/subscription/migrate.
  const startMigration = async () => {
    setStarting(true);
    try {
      const res = await post(
        'user/payment-method/setup-intent',
        { migration: true },
        true
      );
      if (!res?.success || !res?.clientSecret) {
        showSnackbar(res?.message || 'Could not start the migration.', 'error');
        return;
      }
      setPayment({
        clientSecret: res.clientSecret,
        publishableKey: res.publishableKey,
        mode: 'migrate'
      });
    } catch (e) {
      showSnackbar('Could not start the migration. Please try again.', 'error');
    } finally {
      setStarting(false);
    }
  };

  const finishUpdate = async () => {
    setPayment(null);
    await fetchData();
  };

  // Init Stripe.js with the account the server issued THIS SetupIntent on (a legacy
  // subscriber's card lives on the legacy account even when new payments go to the
  // primary one), falling back to the primary env key.
  const stripeForForm = useMemo(
    () =>
      payment?.publishableKey
        ? loadStripe(payment.publishableKey)
        : stripePromise,
    [payment?.publishableKey]
  );

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'transactionDate',
      headerName: 'Transaction Date',
      type: 'dateTime',
      width: 150,
      valueFormatter: (value: any) => {
        return new Date(value);
      }
    },
    {
      field: 'amount',
      headerName: 'Amount',
      type: 'number',
      width: 110
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120
    },
    {
      field: 'transactionType',
      headerName: 'Type',
      width: 130
    },
    {
      field: 'stripeTransactionId',
      headerName: 'Stripe ID',
      width: 200
    }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4 // Adds spacing between sections
      }}
    >
      {/* Payment Methods Section */}
      <Box sx={{ px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1.5
          }}
        >
          <Typography variant="h4" sx={{ mb: 0 }}>
            Payment Methods
          </Typography>
          {!payment && (
            <Button
              variant="contained"
              onClick={needsMigration ? startMigration : startUpdate}
              disabled={starting}
            >
              {starting ? (
                <CircularProgress size={20} />
              ) : needsMigration ? (
                'Move to new billing'
              ) : (paymentMethods && paymentMethods.length) ||
                subscriptionOnOldSystem ? (
                // A subscription on the old system HAS a card, just not a listed one.
                'Update card'
              ) : (
                'Add card'
              )}
            </Button>
          )}
        </Box>

        {needsMigration && !payment && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Your subscription still bills on our old system. Move it to the new
            one with your card — no charge today, and your renewal date and
            giveaway entries stay exactly the same.
          </Typography>
        )}

        {/* Where the dashboard's "problem with your card" banner sends them
            (MigrateBillingPrompt). Updating the card retries the unpaid invoice
            (server: setDefaultPaymentMethod), and once it is paid migration-status
            offers "Move to new billing" here. */}
        {migrationReason === 'unpaid' && !payment && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            A payment on your subscription did not go through. Update your card to
            pay it — after that, you can move your subscription to our new billing
            system.
          </Typography>
        )}

        <Grid container spacing={2}>
          {!paymentMethods || !paymentMethods.length ? (
            <Grid item xs={12}>
              <Typography variant="body1">
                You have no saved payment methods.
              </Typography>
            </Grid>
          ) : (
            paymentMethods.map((paymentMethod: any) => (
              <Grid item xs={12} md={6} key={paymentMethod.id}>
                <Box sx={{ ...panelSx, p: 2.25 }}>
                  <Typography sx={{ fontWeight: 700, color: PT.text }}>
                    {paymentMethod.brand} ending in {paymentMethod.last4}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13, color: PT.textMuted, mt: 0.5 }}
                  >
                    Expiry: {paymentMethod.expMonth}/{paymentMethod.expYear}
                  </Typography>
                </Box>
              </Grid>
            ))
          )}
        </Grid>

        {subscriptionOnOldSystem && !payment && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            The card your subscription currently uses is on our old billing
            system, so it isn’t shown here.
          </Typography>
        )}

        {/* Card-update form (SetupIntent + Stripe Elements) */}
        {payment && (
          <Elements
            stripe={stripeForForm}
            options={{
              clientSecret: payment.clientSecret,
              appearance: {
                theme: 'night',
                variables: {
                  colorPrimary: PT.teal,
                  colorBackground: PT.surface,
                  colorText: PT.text,
                  borderRadius: '10px',
                  fontFamily: 'Montserrat, sans-serif'
                }
              }
            }}
          >
            <UpdateCardForm
              mode={payment.mode}
              renewsOnOldSystem={subscriptionOnOldSystem && !needsMigration}
              onDone={finishUpdate}
              onCancel={() => setPayment(null)}
            />
          </Elements>
        )}
      </Box>

      {/* Transactions Section */}
      <Box sx={{ px: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>
          Transactions
        </Typography>

        <Box sx={{ height: 400, width: '100%' }}>
          <StyledDataGrid
            rows={transactions}
            columns={columns}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>
    </Box>
  );
};
